import { startTransition } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, RefreshCw, ShieldCheck } from "lucide-react";
import Button from "../../../components/common/Button/Button";
import StickyActionBar from "../../../components/common/stickyActionBar/StickyActionBar";
import { Card } from "../../../components/ui/card";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import "./RegisterSuccessPage.css";

// Eight dots around the mark, alternating brand blue and success green.
// Angle and travel vary a little so the burst reads as scattered, not a clock.
const BURST = [
  { angle: 0, travel: 58, color: "#00A9E7", size: 7 },
  { angle: 45, travel: 52, color: "#1E8E5A", size: 5 },
  { angle: 90, travel: 60, color: "#00A9E7", size: 6 },
  { angle: 135, travel: 50, color: "#55C6EF", size: 5 },
  { angle: 180, travel: 58, color: "#1E8E5A", size: 7 },
  { angle: 225, travel: 52, color: "#00A9E7", size: 5 },
  { angle: 270, travel: 60, color: "#55C6EF", size: 6 },
  { angle: 315, travel: 50, color: "#1E8E5A", size: 5 },
];

const NEXT_STEPS = [
  {
    icon: ShieldCheck,
    en: "See all your policies in one place",
    ar: "عرض جميع وثائقك في مكان واحد",
  },
  {
    icon: FileText,
    en: "File and track your claims",
    ar: "تقديم مطالباتك ومتابعة حالتها",
  },
  {
    icon: RefreshCw,
    en: "Renew in a few taps",
    ar: "تجديد وثائقك بخطوات بسيطة",
  },
];

/**
 * Last step of sign-up: the account is ready.
 *
 * All copy lives here (no CMS), so the page can never render empty. A light
 * page in the flow's own palette; the one celebratory moment is the mark
 * (see RegisterSuccessPage.css), because this happens once per customer.
 * Below it, what the account unlocks, so the finish line points somewhere.
 */
const RegisterSuccessPage = () => {
  const navigate = useNavigate();
  const translate = useLanguageTranslate();

  const signIn = () => {
    startTransition(() => {
      navigate("/sign-in");
    });
  };

  return (
    <div className="register_success_page mx-auto w-[500px] max-w-full px-5 pt-24 pb-10 max767:pt-[96px]">
      {/* Success mark */}
      <div className="relative mx-auto mb-7 flex h-24 w-24 items-center justify-center">
        <span
          aria-hidden="true"
          className="rs-ripple absolute inset-0 rounded-full border-2 border-[#1E8E5A]/40"
        />
        {BURST.map((dot, index) => (
          <span
            key={index}
            aria-hidden="true"
            className="rs-dot absolute left-1/2 top-1/2 rounded-full"
            style={{
              width: dot.size,
              height: dot.size,
              marginLeft: -dot.size / 2,
              marginTop: -dot.size / 2,
              backgroundColor: dot.color,
              "--rs-angle": `${dot.angle}deg`,
              "--rs-travel": `${dot.travel}px`,
            }}
          />
        ))}
        <span className="rs-pop relative flex h-24 w-24 items-center justify-center rounded-full bg-[#E8F7EF]">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#1E8E5A] shadow-[0_6px_16px_rgba(30,142,90,0.28)]">
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="h-7 w-7"
              fill="none"
              stroke="#fff"
              strokeWidth="2.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path className="rs-check" d="M5 12.5l4.5 4.5L19 7.5" />
            </svg>
          </span>
        </span>
      </div>

      <div className="rs-rise text-center" style={{ animationDelay: "140ms" }}>
        <h1 className="text-[#1A2024] text-[28px] font-semibold leading-9 tracking-[-0.28px] [text-wrap:balance]">
          {translate("Welcome to Solidarity", "مرحبًا بك في سوليدرتي")}
        </h1>
        <p className="mx-auto mt-2 max-w-[42ch] text-[#5B6871] text-base leading-6 tracking-[-0.16px] [text-wrap:balance]">
          {translate(
            "Your account is ready. Sign in to get started.",
            "تم إنشاء حسابك بنجاح. سجّل الدخول للمتابعة."
          )}
        </p>
      </div>

      {/* What the account unlocks */}
      <Card className="mt-8 overflow-hidden rounded-[14px] border-[#E5E9EB] shadow-none">
        <p
          className="rs-rise px-5 pt-4 pb-1 text-[#5B6871] text-sm font-medium leading-5"
          style={{ animationDelay: "220ms" }}
        >
          {translate("What you can do now", "يمكنك الآن:")}
        </p>
        <ul>
          {NEXT_STEPS.map(({ icon: Icon, en, ar }, index) => (
            <li
              key={en}
              className="rs-rise flex items-center gap-3 px-5 py-3 [&:not(:last-child)]:border-b [&:not(:last-child)]:border-[#F0F2F3]"
              style={{ animationDelay: `${280 + index * 60}ms` }}
            >
              <span
                aria-hidden="true"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#E6F6FD]"
              >
                <Icon className="h-[18px] w-[18px] text-[#0A7CAE]" />
              </span>
              <span className="text-[#1A2024] text-[15px] font-medium leading-6">
                {translate(en, ar)}
              </span>
            </li>
          ))}
        </ul>
      </Card>

      <div className="mt-8">
        <StickyActionBar>
          <Button
            label={translate("Sign in", "تسجيل الدخول")}
            className="primary-button !mb-0"
            onSubmit={signIn}
          />
        </StickyActionBar>
      </div>
    </div>
  );
};

export default RegisterSuccessPage;
